import React, { useCallback, useEffect, useState } from 'react';
import i18n from '../../../i18n';
import { Link, useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import hotkeys from 'hotkeys-js';

import { useAction } from 'core/store/store';

import { makeAuthLoginRequestAction } from 'state/auth/actions';

import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { Text, TextTypography } from 'components/Text/Text';

import { LoginForm } from 'forms/LoginForm/LoginForm';

import { RoutePath } from 'RoutePath';

import { UserRole } from 'types';

import { NoAccount } from './components/NoAccount';
import { AuthHeader } from './components/AuthHeader';
import { AdminLogin } from './components/AdminLogin';

const useStyles = makeStyles(theme => ({
    container: {
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        position: 'relative',
    },
    contents: {
        flex: 1,
        width: 360,
        [theme.breakpoints.down(480)]: {
            width: '100%',
            padding: theme.spacing(2),
        },
    },
    footer: {
        width: '100vw',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: theme.palette.fixedSuccess.accent,
    },
    sketch: {
        marginTop: theme.spacing(7),
        [theme.breakpoints.down(361)]: {
            marginTop: theme.spacing(2.5),
        },
    },
    title: {
        [theme.breakpoints.down(360)]: {
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(1.5),
        },
    },
    backDesktop: {
        position: 'absolute',
        top: 24,
        left: 40,
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.fixedPrimary.primary,
        cursor: 'pointer',
        width: 100,
    },
    backDesktopIcon: {
        marginRight: 8,
    },
    loginAsAdmin: {
        position: 'absolute',
        top: 12,
        right: 12,
        cursor: 'pointer',
        borderRadius: 10,
        width: 32,
        height: 28,
        fontSize: 20,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: theme.spacing(1),
        color: theme.palette.fixedWhite.primary,
        backgroundColor: theme.palette.fixedDanger.main,
        '&:hover': {
            opacity: 0.8
        },
    }
}));

export const Login = () => {
    const { t } = i18n.useTranslation();
    const action = useAction();
    const history = useHistory();
    const classes = useStyles();

    const [isAdminLoginDialogOpened, setIsAdminLoginDialogOpened] = useState(false);
    const [isLoginAsAdminButtonVisible, setIsLoginAsAdminButtonVisible] = useState(false);

    const handleBackClick = useCallback(() => history.goBack(), [history]);

    const handleLogoClick = useCallback(() => window.location.assign(RoutePath.FEED), [history]);

    const handleSubmit = useCallback(
        data => {
            const { phone, password } = data;
            action(makeAuthLoginRequestAction, phone, password, UserRole.VENDOR);
        },
        [action],
    );

    const handleLoginAsAdmin = () => {
        setIsAdminLoginDialogOpened(true); 
    }

    useEffect(() => {
        const HOTKEY = 'ctrl+alt+l,cmd+alt+l';

        hotkeys(HOTKEY, (event) => {
            event.preventDefault();
            setIsLoginAsAdminButtonVisible(true);
        });
        return () => {
            hotkeys.unbind(HOTKEY)
        }
    }, [])

    return (
        <div className={classes.container}>
            <AuthHeader onLogoClick={handleLogoClick} onBack={handleBackClick} />
            <div className={classes.contents}>
                <Box m={3} textAlign="center" className={classes.title}>
                    <Text typography={TextTypography.LEAD_ACCENT}>{t('auth:login.title')}</Text>
                </Box>
                <LoginForm onSubmit={handleSubmit} />
                <Box mt={1}>
                    <Link to={RoutePath.RESET_PASSWORD}>
                        <LargeButton color={LargeButtonColor.TRANSPARENT}>{t('auth:login.forgotPassword')}</LargeButton>
                    </Link>
                </Box>
            </div>
            <div className={classes.footer}>
                <NoAccount />
            </div>

            {isLoginAsAdminButtonVisible && (
                <div className={classes.loginAsAdmin} onClick={handleLoginAsAdmin}>
                    ...
                </div>
            )}
            {isAdminLoginDialogOpened && (
                <AdminLogin onClose={() => setIsAdminLoginDialogOpened(false)} />
            )}
        </div>
    );
};
